import { FC } from 'react';

import type { ActivityCounts, ActivityEntryKind } from '@/api/gamma-team/types';
import { theme, Typography } from '@/design-system';

import { SkeletonBar, StatCard, StatCardButton, StatCardLabel, StatCardRow } from './styled';

export const SUMMARY_CARDS: {
  key: keyof ActivityCounts;
  label: string;
  kind?: ActivityEntryKind;
}[] = [
  { key: 'contactsReached', label: 'Contacts reached' },
  { key: 'entries', label: 'Entries' },
  { key: 'calls', label: 'Calls', kind: 'Call' },
  { key: 'messages', label: 'Messages', kind: 'Message' },
  { key: 'meetings', label: 'Meetings', kind: 'Meeting' },
  { key: 'notes', label: 'Notes', kind: 'Note' },
  { key: 'dealsCreated', label: 'Deals created' },
  { key: 'dealsAdvanced', label: 'Deals advanced' },
];

interface SummaryCardsProps {
  counts: ActivityCounts | null;
  selectedKind: ActivityEntryKind | null;
  onToggleKind: (_kind: ActivityEntryKind) => void;
}

export const SummaryCards: FC<SummaryCardsProps> = ({ counts, selectedKind, onToggleKind }) => (
  <StatCardRow>
    {SUMMARY_CARDS.map(({ key, label, kind }) => {
      const content = (
        <>
          <StatCardLabel>
            <Typography variant="body2" fontWeight={400} color={theme.colors.text.secondary} noWrap>
              {label}
            </Typography>
          </StatCardLabel>
          {counts ? (
            <Typography variant="h5" fontWeight={600} color={theme.colors.text.primary}>
              {counts[key].toLocaleString()}
            </Typography>
          ) : (
            <SkeletonBar w="48px" h={24} />
          )}
        </>
      );

      if (!kind) return <StatCard key={key}>{content}</StatCard>;

      const selected = selectedKind === kind;

      return (
        <StatCardButton
          key={key}
          type="button"
          aria-pressed={selected}
          aria-label={selected ? `Clear the ${label} filter` : `Show only ${label}`}
          title={selected ? 'Click to clear the filter' : `Show only ${label}`}
          onClick={() => onToggleKind(kind)}
        >
          {content}
        </StatCardButton>
      );
    })}
  </StatCardRow>
);
